import { useState, useMemo } from 'react'
import { useDepartment } from '../../context/DepartmentContext'
import {
  getInventoryItems,
  saveInventoryItem,
  adjustStock,
  getStockMovements,
  getWastageLog,
  saveWastageEntry,
  getStocktakes,
  saveStocktake,
  savePosFlag,
} from '../../lib/posStore'
import {
  Package,
  Plus,
  Pencil,
  Trash2,
  Search,
  AlertTriangle,
  ArrowUpDown,
  TrendingDown,
  ClipboardList,
  Filter,
  Check,
  X,
  RefreshCw,
} from 'lucide-react'

const TABS = [
  { id: 'stock-levels', label: 'Stock Levels', icon: Package },
  { id: 'stock-movements', label: 'Stock Movements', icon: ArrowUpDown },
  { id: 'wastage', label: 'Wastage', icon: TrendingDown },
  { id: 'stocktake', label: 'Stocktake', icon: ClipboardList },
]

const LOCATIONS = ['kitchen', 'bakery', 'storeroom']
const UNITS = ['kg', 'g', 'L', 'ml', 'pieces', 'slices']
const WASTAGE_REASONS = ['spoiled', 'over-prepared', 'dropped', 'expired', 'other']

const emptyItem = {
  name: '',
  unit: 'kg',
  location: 'kitchen',
  currentStock: 0,
  reorderLevel: 0,
  reorderQty: 0,
  lastPurchasePrice: 0,
}

export default function Inventory() {
  const [tab, setTab] = useState('stock-levels')
  const [refreshKey, setRefreshKey] = useState(0)
  const refresh = () => setRefreshKey(k => k + 1)

  return (
    <div className="min-h-screen bg-gray-950 p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Package size={24} />
            Inventory &amp; Stock Tracking
          </h1>
          <p className="text-gray-400 text-sm mt-1">Manage stock levels, movements, wastage and stocktakes</p>
        </div>
        <button
          onClick={refresh}
          className="flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white text-sm"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                tab === t.id ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <Icon size={14} />
              {t.label}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      {tab === 'stock-levels' && <StockLevelsTab key={`sl-${refreshKey}`} />}
      {tab === 'stock-movements' && <StockMovementsTab key={`sm-${refreshKey}`} />}
      {tab === 'wastage' && <WastageTab key={`w-${refreshKey}`} />}
      {tab === 'stocktake' && <StocktakeTab key={`st-${refreshKey}`} />}
    </div>
  )
}

/* ─────────────────────────────────────────────
   TAB 1: Stock Levels
   ───────────────────────────────────────────── */
function StockLevelsTab() {
  const { currentDepartmentId } = useDepartment()
  const [search, setSearch] = useState('')
  const [locationFilter, setLocationFilter] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [adjustingId, setAdjustingId] = useState(null)
  const [adjustQty, setAdjustQty] = useState('')
  const [adjustReason, setAdjustReason] = useState('')
  const [items, setItems] = useState(() => getInventoryItems(currentDepartmentId))

  const filtered = useMemo(() => {
    let list = locationFilter ? items.filter(i => i.location === locationFilter) : items
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(i => i.name?.toLowerCase().includes(q))
    }
    return list
  }, [items, search, locationFilter])

  function getStatus(item) {
    const stock = Number(item.currentStock) || 0
    const reorder = Number(item.reorderLevel) || 0
    if (stock === 0) return { label: 'Critical', color: 'text-red-400 bg-red-400/10' }
    if (stock <= reorder) return { label: 'Low', color: 'text-amber-400 bg-amber-400/10' }
    return { label: 'Sufficient', color: 'text-green-400 bg-green-400/10' }
  }

  function handleSaveItem(item) {
    const saved = saveInventoryItem({ ...item, departmentId: currentDepartmentId })
    setItems(getInventoryItems(currentDepartmentId))
    setShowForm(false)
    setEditItem(null)
    return saved
  }

  function handleAdjust(itemId) {
    const qty = Number(adjustQty)
    if (!qty || !adjustReason.trim()) return
    adjustStock(itemId, qty, adjustReason.trim())
    setItems(getInventoryItems(currentDepartmentId))
    setAdjustingId(null)
    setAdjustQty('')
    setAdjustReason('')
  }

  return (
    <div className="space-y-4">
      {/* Controls row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search items..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-brand-600"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-500" />
          <select
            value={locationFilter}
            onChange={e => setLocationFilter(e.target.value)}
            className="bg-gray-900 border border-gray-800 rounded-lg text-white text-sm px-3 py-2 focus:outline-none focus:border-brand-600"
          >
            <option value="">All Locations</option>
            {LOCATIONS.map(l => (
              <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => { setEditItem(null); setShowForm(true) }}
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={14} />
          Add Item
        </button>
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <ItemForm
          item={editItem}
          onSave={handleSaveItem}
          onCancel={() => { setShowForm(false); setEditItem(null) }}
        />
      )}

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-4 py-3 font-medium">Item Name</th>
              <th className="px-4 py-3 font-medium">Unit</th>
              <th className="px-4 py-3 font-medium">Current Stock</th>
              <th className="px-4 py-3 font-medium">Reorder Level</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Last Price</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  No inventory items found. Add your first item to get started.
                </td>
              </tr>
            ) : (
              filtered.map(item => {
                const status = getStatus(item)
                const isAdjusting = adjustingId === item.id
                return (
                  <tr key={item.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="px-4 py-3 text-white font-medium">
                      {item.name}
                      <span className="ml-2 text-xs text-gray-500">{item.location}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-300">{item.unit}</td>
                    <td className="px-4 py-3 text-white font-mono">{Number(item.currentStock || 0).toFixed(1)}</td>
                    <td className="px-4 py-3 text-gray-400 font-mono">{Number(item.reorderLevel || 0).toFixed(1)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>
                        {status.label === 'Critical' && <AlertTriangle size={12} />}
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-300 font-mono">
                      {item.lastPurchasePrice ? `R${Number(item.lastPurchasePrice).toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {isAdjusting ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            placeholder="+/- qty"
                            value={adjustQty}
                            onChange={e => setAdjustQty(e.target.value)}
                            className="w-20 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-brand-600"
                          />
                          <input
                            type="text"
                            placeholder="Reason"
                            value={adjustReason}
                            onChange={e => setAdjustReason(e.target.value)}
                            className="w-32 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-brand-600"
                          />
                          <button
                            onClick={() => handleAdjust(item.id)}
                            className="p-1 text-green-400 hover:text-green-300"
                            title="Confirm"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => { setAdjustingId(null); setAdjustQty(''); setAdjustReason('') }}
                            className="p-1 text-red-400 hover:text-red-300"
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setAdjustingId(item.id)}
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                            title="Adjust stock"
                          >
                            <ArrowUpDown size={14} />
                          </button>
                          <button
                            onClick={() => { setEditItem(item); setShowForm(true) }}
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                            title="Edit item"
                          >
                            <Pencil size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ── Item Form (Add / Edit) ── */
function ItemForm({ item, onSave, onCancel }) {
  const [form, setForm] = useState(item ? { ...item } : { ...emptyItem })
  const isEdit = !!item

  function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim()) return
    onSave({
      ...form,
      currentStock: Number(form.currentStock) || 0,
      reorderLevel: Number(form.reorderLevel) || 0,
      reorderQty: Number(form.reorderQty) || 0,
      lastPurchasePrice: Number(form.lastPurchasePrice) || 0,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-4">
      <h3 className="text-white font-medium flex items-center gap-2">
        {isEdit ? <Pencil size={16} /> : <Plus size={16} />}
        {isEdit ? 'Edit Inventory Item' : 'Add Inventory Item'}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-gray-400 text-xs mb-1">Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
            required
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs mb-1">Unit</label>
          <select
            value={form.unit}
            onChange={e => setForm({ ...form, unit: e.target.value })}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
          >
            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-gray-400 text-xs mb-1">Location</label>
          <select
            value={form.location}
            onChange={e => setForm({ ...form, location: e.target.value })}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
          >
            {LOCATIONS.map(l => (
              <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-gray-400 text-xs mb-1">Reorder Level</label>
          <input
            type="number"
            step="0.1"
            value={form.reorderLevel}
            onChange={e => setForm({ ...form, reorderLevel: e.target.value })}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs mb-1">Reorder Qty</label>
          <input
            type="number"
            step="0.1"
            value={form.reorderQty}
            onChange={e => setForm({ ...form, reorderQty: e.target.value })}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
          />
        </div>
        {!isEdit && (
          <div>
            <label className="block text-gray-400 text-xs mb-1">Opening Stock</label>
            <input
              type="number"
              step="0.1"
              value={form.currentStock}
              onChange={e => setForm({ ...form, currentStock: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
            />
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Check size={14} />
          {isEdit ? 'Update Item' : 'Add Item'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 text-gray-300 hover:text-white rounded-lg text-sm transition-colors"
        >
          <X size={14} />
          Cancel
        </button>
      </div>
    </form>
  )
}

/* ─────────────────────────────────────────────
   TAB 2: Stock Movements
   ───────────────────────────────────────────── */
function StockMovementsTab() {
  const { currentDepartmentId } = useDepartment()
  const allItems = useMemo(() => getInventoryItems(currentDepartmentId), [currentDepartmentId])
  const [itemFilter, setItemFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const movements = useMemo(() => {
    return getStockMovements(
      currentDepartmentId,
      itemFilter || undefined,
      dateFrom || undefined,
      dateTo || undefined
    )
  }, [currentDepartmentId, itemFilter, dateFrom, dateTo])

  const itemMap = useMemo(() => {
    const map = {}
    allItems.forEach(i => { map[i.id] = i })
    return map
  }, [allItems])

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div>
          <label className="block text-gray-400 text-xs mb-1">From Date</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs mb-1">To Date</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs mb-1">Item</label>
          <select
            value={itemFilter}
            onChange={e => setItemFilter(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
          >
            <option value="">All Items</option>
            {allItems.map(i => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Item</th>
              <th className="px-4 py-3 font-medium">Qty Change</th>
              <th className="px-4 py-3 font-medium">Reason</th>
              <th className="px-4 py-3 font-medium">Balance After</th>
            </tr>
          </thead>
          <tbody>
            {movements.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  No stock movements found for the selected filters.
                </td>
              </tr>
            ) : (
              movements.map(m => {
                const isPositive = m.qtyChange > 0
                return (
                  <tr key={m.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="px-4 py-3 text-gray-300">{m.date}</td>
                    <td className="px-4 py-3 text-white">{itemMap[m.itemId]?.name || m.itemId}</td>
                    <td className={`px-4 py-3 font-mono font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                      {isPositive ? '+' : ''}{Number(m.qtyChange).toFixed(1)}
                    </td>
                    <td className="px-4 py-3 text-gray-400">{m.reason}</td>
                    <td className="px-4 py-3 text-white font-mono">{Number(m.balanceAfter).toFixed(1)}</td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────
   TAB 3: Wastage
   ───────────────────────────────────────────── */
function WastageTab() {
  const { currentDepartmentId } = useDepartment()
  const allItems = useMemo(() => getInventoryItems(currentDepartmentId), [currentDepartmentId])
  const [wastageLog, setWastageLog] = useState(() => getWastageLog(currentDepartmentId))
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    inventoryItemId: '',
    qty: '',
    reason: 'spoiled',
  })

  const itemMap = useMemo(() => {
    const map = {}
    allItems.forEach(i => { map[i.id] = i })
    return map
  }, [allItems])

  const totalWastage = useMemo(() => {
    return wastageLog.reduce((sum, w) => sum + (Number(w.qty) || 0), 0)
  }, [wastageLog])

  function handleSubmit(e) {
    e.preventDefault()
    if (!form.inventoryItemId || !form.qty || Number(form.qty) <= 0) return
    saveWastageEntry({
      departmentId: currentDepartmentId,
      date: form.date,
      inventoryItemId: form.inventoryItemId,
      qty: Number(form.qty),
      reason: form.reason,
      itemName: itemMap[form.inventoryItemId]?.name || '',
      unit: itemMap[form.inventoryItemId]?.unit || '',
    })
    setWastageLog(getWastageLog(currentDepartmentId))
    setForm({ date: new Date().toISOString().split('T')[0], inventoryItemId: '', qty: '', reason: 'spoiled' })
  }

  return (
    <div className="space-y-4">
      {/* Log Wastage Form */}
      <form onSubmit={handleSubmit} className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-4">
        <h3 className="text-white font-medium flex items-center gap-2">
          <TrendingDown size={16} />
          Log Wastage
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-gray-400 text-xs mb-1">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1">Item *</label>
            <select
              value={form.inventoryItemId}
              onChange={e => setForm({ ...form, inventoryItemId: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
              required
            >
              <option value="">Select item...</option>
              {allItems.map(i => (
                <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1">Quantity *</label>
            <input
              type="number"
              step="0.1"
              min="0.1"
              value={form.qty}
              onChange={e => setForm({ ...form, qty: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
              required
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1">Reason</label>
            <select
              value={form.reason}
              onChange={e => setForm({ ...form, reason: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
            >
              {WASTAGE_REASONS.map(r => (
                <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1).replace('-', ' ')}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          type="submit"
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={14} />
          Log Wastage
        </button>
      </form>

      {/* Summary */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <p className="text-gray-400 text-sm">
          Total wastage entries: <span className="text-white font-medium">{wastageLog.length}</span>
          <span className="mx-3 text-gray-700">|</span>
          Total qty wasted: <span className="text-red-400 font-medium">{totalWastage.toFixed(1)}</span>
        </p>
      </div>

      {/* Wastage History */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Item</th>
              <th className="px-4 py-3 font-medium">Quantity</th>
              <th className="px-4 py-3 font-medium">Unit</th>
              <th className="px-4 py-3 font-medium">Reason</th>
            </tr>
          </thead>
          <tbody>
            {wastageLog.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  No wastage entries recorded yet.
                </td>
              </tr>
            ) : (
              wastageLog.map(w => (
                <tr key={w.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="px-4 py-3 text-gray-300">{w.date}</td>
                  <td className="px-4 py-3 text-white">{w.itemName || itemMap[w.inventoryItemId]?.name || w.inventoryItemId}</td>
                  <td className="px-4 py-3 text-red-400 font-mono font-medium">{Number(w.qty).toFixed(1)}</td>
                  <td className="px-4 py-3 text-gray-400">{w.unit || itemMap[w.inventoryItemId]?.unit || ''}</td>
                  <td className="px-4 py-3 text-gray-400 capitalize">{(w.reason || '').replace('-', ' ')}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────
   TAB 4: Stocktake
   ───────────────────────────────────────────── */
function StocktakeTab() {
  const { currentDepartmentId } = useDepartment()
  const allItems = useMemo(() => getInventoryItems(currentDepartmentId), [currentDepartmentId])
  const [stocktakes, setStocktakes] = useState(() => getStocktakes(currentDepartmentId))
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [counts, setCounts] = useState(() => {
    const map = {}
    allItems.forEach(i => { map[i.id] = '' })
    return map
  })
  const [saved, setSaved] = useState(false)

  function getVariance(item) {
    const physical = Number(counts[item.id])
    if (counts[item.id] === '' || isNaN(physical)) return null
    const system = Number(item.currentStock) || 0
    const diff = physical - system
    const pct = system !== 0 ? (diff / system) * 100 : (physical !== 0 ? 100 : 0)
    return { diff, pct, physical, system }
  }

  function handleSave() {
    const lines = allItems.map(item => {
      const v = getVariance(item)
      return {
        inventoryItemId: item.id,
        itemName: item.name,
        unit: item.unit,
        systemCount: Number(item.currentStock) || 0,
        physicalCount: v ? v.physical : null,
        variance: v ? v.diff : null,
        variancePct: v ? v.pct : null,
      }
    }).filter(l => l.physicalCount !== null)

    if (lines.length === 0) return

    const stocktake = saveStocktake({
      departmentId: currentDepartmentId,
      date,
      lines,
      totalItems: lines.length,
      flaggedItems: lines.filter(l => Math.abs(l.variancePct) > 5).length,
    })

    // Generate flags for large variances
    lines.forEach(l => {
      if (Math.abs(l.variancePct) > 5) {
        savePosFlag({
          departmentId: currentDepartmentId,
          flagCode: 'STOCKTAKE_VARIANCE',
          severity: Math.abs(l.variancePct) > 20 ? 'HIGH' : 'MEDIUM',
          title: `Stocktake variance: ${l.itemName}`,
          description: `${l.variancePct.toFixed(1)}% variance detected (system: ${l.systemCount}, physical: ${l.physicalCount})`,
          date,
          productId: l.inventoryItemId,
        })
      }
    })

    setStocktakes(getStocktakes(currentDepartmentId))
    setSaved(true)
  }

  return (
    <div className="space-y-4">
      {/* Date + Save */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div>
          <label className="block text-gray-400 text-xs mb-1">Stocktake Date</label>
          <input
            type="date"
            value={date}
            onChange={e => { setDate(e.target.value); setSaved(false) }}
            className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-brand-600"
          />
        </div>
        <button
          onClick={handleSave}
          disabled={saved}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            saved
              ? 'bg-green-700/30 text-green-400 cursor-default'
              : 'bg-brand-600 hover:bg-brand-700 text-white'
          }`}
        >
          {saved ? <Check size={14} /> : <ClipboardList size={14} />}
          {saved ? 'Stocktake Saved' : 'Save Stocktake'}
        </button>
      </div>

      {/* Count Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-4 py-3 font-medium">Item</th>
              <th className="px-4 py-3 font-medium">System Count</th>
              <th className="px-4 py-3 font-medium">Physical Count</th>
              <th className="px-4 py-3 font-medium">Variance</th>
              <th className="px-4 py-3 font-medium">% Variance</th>
            </tr>
          </thead>
          <tbody>
            {allItems.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  No inventory items to count. Add items in the Stock Levels tab first.
                </td>
              </tr>
            ) : (
              allItems.map(item => {
                const v = getVariance(item)
                const hasHighVariance = v && Math.abs(v.pct) > 5
                return (
                  <tr
                    key={item.id}
                    className={`border-b border-gray-800/50 ${hasHighVariance ? 'bg-red-900/10' : 'hover:bg-gray-800/30'}`}
                  >
                    <td className="px-4 py-3 text-white">
                      {item.name}
                      <span className="ml-2 text-xs text-gray-500">({item.unit})</span>
                    </td>
                    <td className="px-4 py-3 text-gray-300 font-mono">
                      {Number(item.currentStock || 0).toFixed(1)}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={counts[item.id]}
                        onChange={e => {
                          setCounts({ ...counts, [item.id]: e.target.value })
                          setSaved(false)
                        }}
                        placeholder="Enter count"
                        className="w-28 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white text-sm font-mono focus:outline-none focus:border-brand-600"
                      />
                    </td>
                    <td className={`px-4 py-3 font-mono font-medium ${
                      v === null ? 'text-gray-600' : hasHighVariance ? 'text-red-400' : 'text-gray-300'
                    }`}>
                      {v !== null ? (v.diff > 0 ? '+' : '') + v.diff.toFixed(1) : '—'}
                    </td>
                    <td className={`px-4 py-3 font-mono font-medium ${
                      v === null ? 'text-gray-600' : hasHighVariance ? 'text-red-400' : 'text-gray-300'
                    }`}>
                      {v !== null ? (
                        <span className="flex items-center gap-1">
                          {hasHighVariance && <AlertTriangle size={12} className="text-red-400" />}
                          {v.pct.toFixed(1)}%
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Past Stocktakes */}
      {stocktakes.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
          <h3 className="text-white font-medium text-sm">Previous Stocktakes</h3>
          <div className="space-y-2">
            {stocktakes.slice(0, 10).map(st => (
              <div
                key={st.id}
                className="flex items-center justify-between px-3 py-2 bg-gray-800/50 rounded-lg text-sm"
              >
                <span className="text-gray-300">{st.date}</span>
                <span className="text-gray-400">{st.totalItems} items counted</span>
                <span className={st.flaggedItems > 0 ? 'text-red-400' : 'text-green-400'}>
                  {st.flaggedItems > 0 ? (
                    <span className="flex items-center gap-1">
                      <AlertTriangle size={12} />
                      {st.flaggedItems} flagged
                    </span>
                  ) : (
                    'All clear'
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
